// Backend services emit timestamps in different shapes:
// - Go's default time.Time#String() format, e.g. "2024-11-18 07:24:58.067943 +0000 UTC"
// - Proper ISO-8601, e.g. "2024-11-12T18:27:07Z"
// Both are always UTC, but native `Date` parsing of the first form is inconsistent across
// browsers (V8 is lenient, Safari/Firefox are not), and passing either raw string straight to
// Mixpanel means it is stored as plain text rather than a recognized date. That produces the
// "Time" vs "Time Processed (UTC)" mismatch users see on events/profiles. Normalize everything
// to a single, explicit, unambiguous UTC ISO-8601 string before it reaches Mixpanel.
const GO_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(?:\s*([+-]\d{2}:?\d{2}|Z))?\s*(UTC)?$/;

/**
 * Normalize a timestamp of unknown shape (Go time string, ISO-8601 string, epoch number, or
 * Date) into a canonical UTC ISO-8601 string (e.g. "2024-11-18T07:24:58.067Z").
 *
 * Returns undefined for null/empty/unparseable input so callers can omit the property instead
 * of sending a garbage value to Mixpanel.
 */
export function toUtcIso(value: string | number | Date | null | undefined): string | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }

  if (typeof value === 'number') {
    // Support both seconds-since-epoch and ms-since-epoch inputs.
    const ms = Math.abs(value) < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const match = trimmed.match(GO_TIMESTAMP_PATTERN);
  if (match) {
    const [, year, month, day, hour, minute, second, fraction, offset] = match;
    // Every timestamp of this shape observed from our services is UTC (explicit "+0000 UTC" /
    // "Z" / no offset at all defaults to UTC too). Build the ISO string manually rather than
    // relying on browser-specific leniency for non-standard formats.
    if (offset && offset !== 'Z' && offset !== '+00:00' && offset !== '+0000') {
      // Unexpected non-UTC offset; fall back to native parsing rather than silently assuming UTC.
      const fallback = new Date(trimmed);
      return Number.isNaN(fallback.getTime()) ? undefined : fallback.toISOString();
    }
    // Truncate (not round) to millisecond precision: Go's fractional seconds are sub-millisecond
    // accurate, and rounding "067943" up to "068" would shift the timestamp by a millisecond.
    const millis = fraction ? fraction.padEnd(3, '0').slice(0, 3) : '000';
    const isoCandidate = `${year}-${month}-${day}T${hour}:${minute}:${second}.${millis}Z`;
    const date = new Date(isoCandidate);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}
